/**
 * Tiny, dependency-free markdown→React renderer.
 * Handles: h2, h3, h4, paragraphs, bullet lists, ordered lists, bold (**),
 * italic (*), inline code (`), and links [text](url). Sufficient for our
 * curated blog posts; not a full CommonMark implementation.
 */
import { Link } from 'react-router-dom';

const inline = (text, keyPrefix = 'k') => {
  // bold **x** → strong
  let parts = [text];
  const bold = /\*\*(.+?)\*\*/g;
  const link = /\[(.+?)\]\((.+?)\)/g;
  const italic = /(?<!\*)\*(?!\*)(.+?)(?<!\*)\*(?!\*)/g;
  const code = /`(.+?)`/g;
  // We do a tokenizer pass for each rule. Order matters: links → bold → italic → code.
  const replace = (arr, regex, build) =>
    arr.flatMap((p) => {
      if (typeof p !== 'string') return [p];
      const out = [];
      let last = 0;
      let m;
      const re = new RegExp(regex.source, regex.flags);
      while ((m = re.exec(p)) !== null) {
        if (m.index > last) out.push(p.slice(last, m.index));
        out.push(build(m, out.length));
        last = m.index + m[0].length;
      }
      if (last < p.length) out.push(p.slice(last));
      return out;
    });

  parts = replace(parts, link, (m, i) =>
    m[2].startsWith('http') ? (
      <a key={`${keyPrefix}-a-${i}`} href={m[2]} className="text-[#F97316] hover:underline">
        {m[1]}
      </a>
    ) : (
      <Link key={`${keyPrefix}-a-${i}`} to={m[2]} className="text-[#F97316] hover:underline">
        {m[1]}
      </Link>
    )
  );
  parts = replace(parts, bold, (m, i) => (
    <strong key={`${keyPrefix}-b-${i}`}>{m[1]}</strong>
  ));
  parts = replace(parts, italic, (m, i) => (
    <em key={`${keyPrefix}-i-${i}`}>{m[1]}</em>
  ));
  parts = replace(parts, code, (m, i) => (
    <code key={`${keyPrefix}-c-${i}`} className="px-1.5 py-0.5 bg-[#1C1917]/5 rounded text-sm">
      {m[1]}
    </code>
  ));
  return parts;
};

export const Markdown = ({ children }) => {
  const blocks = String(children || '').trim().split(/\n\s*\n/);
  const els = [];
  blocks.forEach((block, bi) => {
    const lines = block.split('\n');
    // List detection
    if (lines.every((l) => /^\s*-\s+/.test(l))) {
      els.push(
        <ul key={`ul-${bi}`} className="list-disc pl-6 space-y-2 my-4">
          {lines.map((l, i) => (
            <li key={`li-${bi}-${i}`}>{inline(l.replace(/^\s*-\s+/, ''), `li-${bi}-${i}`)}</li>
          ))}
        </ul>
      );
      return;
    }
    if (lines.every((l) => /^\s*\d+\.\s+/.test(l))) {
      els.push(
        <ol key={`ol-${bi}`} className="list-decimal pl-6 space-y-2 my-4">
          {lines.map((l, i) => (
            <li key={`li-${bi}-${i}`}>{inline(l.replace(/^\s*\d+\.\s+/, ''), `oli-${bi}-${i}`)}</li>
          ))}
        </ol>
      );
      return;
    }
    // Heading
    const m4 = block.match(/^####\s+(.+)/);
    const m3 = block.match(/^###\s+(.+)/);
    const m2 = block.match(/^##\s+(.+)/);
    if (m4) {
      els.push(
        <h4 key={`h-${bi}`} className="font-['Playfair_Display'] text-lg font-bold text-[#1C1917] mt-5 mb-2">
          {inline(m4[1], `h-${bi}`)}
        </h4>
      );
      return;
    }
    if (m3) {
      els.push(
        <h3 key={`h-${bi}`} className="font-['Playfair_Display'] text-xl font-bold text-[#1C1917] mt-6 mb-3">
          {inline(m3[1], `h-${bi}`)}
        </h3>
      );
      return;
    }
    if (m2) {
      els.push(
        <h2 key={`h-${bi}`} className="font-['Playfair_Display'] text-2xl sm:text-3xl font-bold text-[#1C1917] mt-8 mb-4">
          {inline(m2[1], `h-${bi}`)}
        </h2>
      );
      return;
    }
    // Paragraph
    els.push(
      <p key={`p-${bi}`} className="text-[#1C1917]/80 leading-relaxed mb-4">
        {inline(block, `p-${bi}`)}
      </p>
    );
  });
  return <div className="prose-content">{els}</div>;
};
